
import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Divider,
  Button,
  Container,
  Alert,
  Chip,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';

const ActiveSubscription = ({ subscriptions, userData, navigate }) => {
  const [eligibleRewards, setEligibleRewards] = useState([]);
  const [loadingReward, setLoadingReward] = useState(true);
  const [rewardError, setRewardError] = useState(null);
  const [referralCounts, setReferralCounts] = useState({
    buyer: { count: 0, type: 'Buyer' },
    tenant: { count: 0, type: 'Tenant' },
    owner: { count: 0, type: 'Individual Owner/Builder' },
    landlord: { count: 0, type: 'Landlord' },
    agent: { count: 0, type: 'Agent' }
  });
  const [rewardModalOpen, setRewardModalOpen] = useState(false);
  const [claimingReward, setClaimingReward] = useState(false);
  const [claimError, setClaimError] = useState(null);
  const [addOnPlans, setAddOnPlans] = useState([]);
  const [loadingAddOns, setLoadingAddOns] = useState(false);
  const [addOnError, setAddOnError] = useState(null);
  const [selectedReward, setSelectedReward] = useState(null);
  const [currentPlan, setCurrentPlan] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [userPlans, setUserPlans] = useState([]);

  useEffect(() => {
    const checkReferralReward = async () => {
      try {
        setLoadingReward(true);

        // 1. Fetch user data
        const userResponse = await fetch(`https://landnest.net:81/users/${userData.user_id}/`);
        if (!userResponse.ok) throw new Error('Failed to fetch user data');
        const user = await userResponse.json();

        // 2. Prepare referral count mapping
        const referralData = {
          buyer: { count: user.no_of_referred_for_buyer || 0, type: 'Buyer' },
          tenant: { count: user.no_of_referred_for_tenant || 0, type: 'Tenant' },
          owner: { count: user.no_of_referred_for_owner || 0, type: 'Individual Owner/Builder' },
          landlord: { count: user.no_of_referred_for_landlord || 0, type: 'Landlord' },
          agent: { count: user.no_of_referred_for_agent || 0, type: 'Agent' }
        };

        setReferralCounts(referralData);

        // 3. Fetch user plans
        const planResponse = await fetch(`https://landnest.net:81/Get-all-plan/${userData.user_id}/`);
        if (!planResponse.ok) throw new Error('Failed to fetch user plans');
        const plans = await planResponse.json();
        setUserPlans(plans);

        // 4. Fetch rewards
        const rewardsResponse = await fetch('https://landnest.net:81/rewards/');
        if (!rewardsResponse.ok) throw new Error('Failed to fetch rewards');
        const rewards = await rewardsResponse.json();

        // 5. Get the most recent reward by rew_id
        const latestReward = rewards.reduce((latest, reward) =>
          (!latest || reward.rew_id > latest.rew_id) ? reward : latest, null);

        // 6. Match the latest reward with referralData and check for non-Free plans
        const matchedRewards = [];

        if (latestReward) {
          Object.entries(referralData).forEach(([key, data]) => {
            if (data.count === Number(latestReward.no_of_users)) {
              // Check if user has a non-Free plan for this user type
              const hasNonFreePlan = plans.some(
                plan => plan.user_type === data.type && 
                       plan.status && 
                       plan.plan_name.toLowerCase() !== 'free'
              );

              if (hasNonFreePlan) {
                matchedRewards.push({
                  ...latestReward,
                  forUserType: data.type
                });
              }
            }
          });
        }

        // 7. Save matched reward(s)
        setEligibleRewards(matchedRewards);

      } catch (err) {
        setRewardError(err.message);
        console.error("Error checking referral reward:", err);
      } finally {
        setLoadingReward(false);
      }
    };

    const fetchAddOnPlans = async () => {
      try {
        setLoadingAddOns(true);
        const response = await fetch(`https://landnest.net:81/Get-addon/${userData.user_id}/`);
        if (!response.ok) throw new Error('Failed to fetch add-on plans');
        const data = await response.json();
        setAddOnPlans(data);
        
        // Also fetch user's current plan to get user_type and sub_id
        const planResponse = await fetch(`https://landnest.net:81/Get-all-plan/${userData.user_id}/`);
        if (planResponse.ok) {
          const plans = await planResponse.json();
          const activePlan = plans.find(plan => plan.status === true);
          if (activePlan) {
            setCurrentPlan({
              user_type: activePlan.user_type,
              sub_id: activePlan.sub_id
            });
          }
        }
      } catch (err) {
        setAddOnError(err.message);
        console.error("Error fetching add-on plans:", err);
      } finally {
        setLoadingAddOns(false);
      }
    };

    if (userData?.user_id) {
      checkReferralReward();
      fetchAddOnPlans();
    }
  }, [userData, refreshKey]);

  const handleClaimReward = async (rewardType) => {
    try {
      console.log('[Claim Reward] Starting reward claim process...');
      console.log('[Claim Reward] Reward type selected:', rewardType);
      console.log('[Claim Reward] Current user ID:', userData.user_id);
      
      setClaimingReward(true);
      setClaimError(null);
      
      // Get the active plan for the user type this reward is for
      const targetUserType = selectedReward.forUserType;
      const targetPlan = subscriptions.find(
        sub => sub.status && sub.user_type === targetUserType
      );

      if (rewardType === 'extend_plan' && !targetPlan) {
        throw new Error(`No active ${targetUserType} plan found to extend`);
      }

      // Prepare payload based on reward type
      let payload;
      if (rewardType === 'Credits') {
        payload = {
          user_id: userData.user_id,
          reward_type: 'Credits',
          credit_points: selectedReward?.credit_points || 0,
          user_type: targetUserType
        };
      } else if (rewardType === 'extend_plan') {
        payload = {
          user_id: userData.user_id,
          reward_type: 'extend_plan',
          sub_id: targetPlan.sub_id,
          user_type: targetUserType
        };
      }
      
      console.log('[Claim Reward] Prepared payload:', payload);

      const response = await fetch('https://landnest.net:81/user-reward/', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload)
      });

      console.log('[Claim Reward] Response status:', response.status);
      
      if (!response.ok) {
        let errorDetails = '';
        try {
          const errorResponse = await response.json();
          errorDetails = errorResponse.message || JSON.stringify(errorResponse);
          console.error('[Claim Reward] Error details:', errorDetails);
        } catch (e) {
          errorDetails = 'Could not parse error response';
        }
        
        throw new Error(`Server responded with status ${response.status}: ${errorDetails}`);
      }

      const result = await response.json();
      console.log('[Claim Reward] Success! Server response:', result);
      
      // Close the modal and reset state
      setRewardModalOpen(false);
      setSelectedReward(null);
      
      // Show success message
      const successMessage = rewardType === 'Credits' 
        ? `Success! ${selectedReward?.credit_points || 0} credit points added to your account`
        : 'Your plan has been extended successfully!';
      
      alert(successMessage);
      
      // Force refresh by incrementing the refreshKey
      setRefreshKey(prev => prev + 1);

      // Refresh the rewards after claiming
      if (userData?.user_id) {
        const userResponse = await fetch(`https://landnest.net:81/users/${userData.user_id}/`);
        if (userResponse.ok) {
          const user = await userResponse.json();
          setReferralCounts({
            buyer: { count: user.no_of_referred_for_buyer || 0, type: 'Buyer' },
            tenant: { count: user.no_of_referred_for_tenant || 0, type: 'Tenant' },
            owner: { count: user.no_of_referred_for_owner || 0, type: 'Individual Owner/Builder' },
            landlord: { count: user.no_of_referred_for_landlord || 0, type: 'Landlord' },
            agent: { count: user.no_of_referred_for_agent || 0, type: 'Agent' }
          });
        }
      }

    } catch (err) {
      console.error('[Claim Reward] Error:', err.message);
      console.error('[Claim Reward] Full error:', err);
      
      let userFriendlyError = 'Failed to claim reward. Please try again.';
      if (err.message.includes('500')) {
        userFriendlyError = 'Server error. Please try again later or contact support.';
      } else if (err.message.includes('401')) {
        userFriendlyError = 'Session expired. Please log in again.';
      } else if (err.message.includes('400')) {
        userFriendlyError = 'Invalid request. Please check your details.';
      } else if (err.message.includes('No active plan found')) {
        userFriendlyError = 'No active plan found to extend. Please purchase a plan first.';
      }
      
      setClaimError(userFriendlyError);
    } finally {
      setClaimingReward(false);
    }
  };

  const renderAddOnFeatures = (addOn) => {
    const userType = addOn.user_type?.toLowerCase();
    
    return (
      <Box component="ul" sx={{ pl: 2, mb: 2 }}>
        {['buyer', 'tenant'].includes(userType) ? (
          <>
            <li>
              Additional owner details : 
              {addOn.no_of_properties_unlimited 
                ? ' Unlimited' 
                : ` ${addOn.no_of_properties || '0'}`}
            </li>
          </>
        ) : (
          <>
            <li>
              Additional property listings: 
              {addOn.no_of_properties_unlimited 
                ? ' Unlimited' 
                : ` ${addOn.no_of_properties || '0'}`}
            </li>
            {addOn.matching_enquiry > 0 && (
              <li>
                Additional buyer inquiries: {addOn.matching_enquiry}
              </li>
            )}
            {addOn.no_of_liked_data > 0 && (
              <li>
                Additional wishlist access: {addOn.no_of_liked_data}
              </li>
            )}
          </>
        )}
      </Box>
    );
  };

  return (
    <Container
      sx={{
        py: 2,
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
        maxWidth: 'sm'
      }}
    >
      <Typography variant="h6" gutterBottom sx={{ textAlign: 'center', mt: 0 }}> Your Active Subscription</Typography>
      
     {loadingReward ? (
        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
          <CircularProgress size={24} />
        </Box>
      ) : eligibleRewards.length > 0 ? (
        <Alert severity="success" sx={{ mb: 2 }}>
          <Typography variant="body1" fontWeight="bold">
            Congratulations! You've earned rewards for referrals!
          </Typography>
          <Box sx={{ mt: 1 }}>
            {eligibleRewards.map((reward, index) => (
              <Box key={index} sx={{ mb: 1, p: 1, backgroundColor: 'rgba(255,255,255,0.2)', borderRadius: 1 }}>
                <Typography>
                  {reward.no_of_users} {reward.forUserType} referrals reward: {reward.credit_points} credit points
                </Typography>
                <Button 
                  size="small" 
                  variant="contained" 
                  color="success"
                  onClick={() => {
                    setSelectedReward(reward);
                    setRewardModalOpen(true);
                  }}
                  sx={{ mt: 1 }}
                >
                  Claim This Reward
                </Button>
              </Box>
            ))}
          </Box>
        </Alert>
      ) : (
        <Alert severity="info" sx={{ mb: 2 }}>
          You will cliam reward only if you have Active Paid subscriptions,
          Your referral counts:
          <Box component="ul" sx={{ pl: 2, mt: 1 }}>
            <li>Buyers: {referralCounts.buyer.count}</li>
            <li>Tenants: {referralCounts.tenant.count}</li>
            <li>Owners: {referralCounts.owner.count}</li>
            <li>Landlords: {referralCounts.landlord.count}</li>
            <li>Agents: {referralCounts.agent.count}</li>
          </Box>
          Keep referring to earn rewards!
        </Alert>
      )}
      
      {rewardError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Error checking referral rewards: {rewardError}
        </Alert>
      )}

      {/* Reward Selection Modal */}
      <Dialog open={rewardModalOpen} onClose={() => setRewardModalOpen(false)}>
        <DialogTitle>Select Your Reward</DialogTitle>
       <DialogContent>
  <Typography variant="body1" sx={{ mb: 2 }}>
    Choose the type of reward you'd like to claim for {selectedReward?.no_of_users} {selectedReward?.forUserType} referrals:
  </Typography>
  
  {claimError && (
    <Alert severity="error" sx={{ mb: 2 }}>
      {claimError}
    </Alert>
  )}
  
  <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', mt: 2 }}>
    <Button 
      variant="contained" 
      color="primary"
      onClick={() => handleClaimReward('Credits')}
      disabled={claimingReward}
    >
      {selectedReward?.credit_points || 0} Credit Points
    </Button>
    <Button 
      variant="contained" 
      color="secondary"
      onClick={() => handleClaimReward('extend_plan')}
      disabled={claimingReward || !subscriptions.some(
        sub => sub.status && sub.user_type === selectedReward?.forUserType
      )}
    >
      Extend {selectedReward?.forUserType} Plan
    </Button>
  </Box>
</DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setRewardModalOpen(false)}
            disabled={claimingReward}
          >
            Cancel
          </Button>
        </DialogActions>
      </Dialog>

      {/* Display all subscriptions */}
      {subscriptions.map((subscription, index) => {
        const userType = subscription.user_type?.toLowerCase();
        const matchingAddOns = addOnPlans.filter(addOn => 
          addOn.user_type.toLowerCase() === userType
        );
        
        return (
          <React.Fragment key={index}>
            <Card 
              sx={{
                borderRadius: '12px',
                boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
                background: subscription.status ? 'rgba(255, 255, 255, 0.8)' : 'rgba(255, 255, 255, 0.6)',
                p: 3,
                mb: 2,
              }}
            >
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="h6" color="primary">
                  {subscription.plan_name} {subscription.user_type} Plan
                </Typography>
                <Chip 
                  label={subscription.status ? 'Active' : 'Inactive'} 
                  color={subscription.status ? 'success' : 'default'} 
                  size="small"
                />
              </Box>
              
              <Table size="small" sx={{ mt: 1 }}>
                <TableBody>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'bold' }}>Price:</TableCell>
                    <TableCell>₹{subscription.charges}</TableCell>
                  </TableRow>
                  {(subscription.plan_name !== 'Lifetime') && (
                    <TableRow>
                      <TableCell sx={{ fontWeight: 'bold' }}>Expiration Date:</TableCell>
                      <TableCell>
                        {new Date(subscription.expired_date).toLocaleDateString('en-GB')}
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>

              <Divider sx={{ my: 2 }} />

              <Typography variant="h6" sx={{ mb: 1 }}>Features:</Typography>
              <Box component="ul" sx={{ pl: 2, mb: 2 }}>
                <li>
                  {['buyer', 'tenant'].includes(userType)
                    ? `Owner details access limit:`
                    : `Property listings limit:`}
                  {subscription.no_of_properties_unlimited 
                    ? ' Unlimited' 
                    : ` ${subscription.no_of_properties || '0'}`}
                </li>

                {['individual owner/builder', 'landlord', 'agent'].includes(userType) && (
                  <>
                    <li>
                      Buyer inquiries access limit:
                      {subscription.matching_enquiry_unlimited 
                        ? ' Unlimited' 
                        : ` ${subscription.matching_enquiry || '0'}`}
                    </li>
                    <li>
                      Wishlist access limit:
                      {subscription.no_of_liked_data_unlimited 
                        ? ' Unlimited' 
                        : ` ${subscription.no_of_liked_data || '0'}`}
                    </li>
                  </>
                )}

                {['buyer', 'tenant'].includes(userType) ? (
                  <>
                    <li>Wishlist any properties</li>
                    <li>Chat with property owners</li>
                  </>
                ) : (
                  <>
                    <li>Property boosting feature</li>
                    <li>Daily wishlist tracking</li>
                  </>
                )}
              </Box>

              {/* Add-on Plans Section */}
              {matchingAddOns.length > 0 && (
                <>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="h6" sx={{ mb: 1 }}>Add-on Plans:</Typography>
                  {matchingAddOns.map((addOn, idx) => (
                    <Box key={idx} sx={{ mb: 2, p: 2, border: '1px solid #e0e0e0', borderRadius: '8px' }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                        {addOn.user_type} Add-on
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        Purchased on: {new Date(addOn.created_at).toLocaleDateString('en-GB')}
                      </Typography>
                      <Typography variant="body2">
                        Price: ₹{addOn.charges}
                      </Typography>
                      {renderAddOnFeatures(addOn)}
                    </Box>
                  ))}
                </>
              )}
            </Card>
          </React.Fragment>
        );
      })}

      <Button 
        variant="outlined" 
        fullWidth
        onClick={() => navigate('/work-detail')}
        sx={{ mt: 2 }}
      >
        Back to Profile
      </Button>
    </Container>
  );
};

export default ActiveSubscription;